/**
 * This file contains service functions for interacting with the Lakana Weather
 * Microservice.
 */

// eslint-disable-next-line camelcase
const apiUrl = window.weatherAlerts?.api_url;

/**
 * Index of weather alert severity to integers, useful for sorting alerts.
 *
 * @see https://docs.google.com/document/d/16cYge1VXtLz68MnZKqsYXnPSOJroN8LdfufK3_ZcWo8/edit# TWC Weather Alerts Detail API documentation
 * @type object
 */
export const severityIndex = {
  Extreme: 1,
  Severe: 2,
  Moderate: 3,
  Minor: 4,
  Unknown: 5,
};

/**
 * Get live alerts for the current site.
 *
 * @param {Array|Null} alerts Pass an array of raw alerts to return them directly;
 *                            any other value will cause alerts to be fetched from
 *                            the site model endpoint.
 * @returns {Promise<*[]>}
 */
export function getLiveAlertsForSite(alerts) {
  if (Array.isArray(alerts)) {
    return Promise.resolve(alerts);
  }

  const counties = window.weatherAlerts && window.weatherAlerts.fips
    ? window.weatherAlerts.fips
    : [];

  if (counties.length) {
    return getLiveAlertsByCounties(counties);
  }

  return Promise.resolve([]);
}

/**
 * Query the alerts microservice by fips codes.
 *
 * @param {Promise}
 */
export function getLiveAlertsByCounties() {
  return fetch(apiUrl)
    .then((response) => response.json())
    .catch(() => []);
}

/**
 * Sort live alerts data by severity.
 *
 * @param {array} alertData Alert data response from microservice.
 * @returns {array} Sorted alert data.
 */
export function sortLiveAlertsBySeverity(alertData) {
  alertData.sort((alertA, alertB) => {
    const sevA = severityIndex[alertA.severity]
      ? severityIndex[alertA.severity]
      : severityIndex.Unknown;
    const sevB = severityIndex[alertB.severity]
      ? severityIndex[alertB.severity]
      : severityIndex.Unknown;

    if (sevA !== sevB) {
      return sevA - sevB;
    }

    if (alertA.areaName < alertB.areaName) {
      return - 1;
    } if (alertA.areaName > alertB.areaName) {
      return 1;
    }

    return 0;
  });

  return alertData;
}

/**
 * Filter live alerts data by alert types that are
 * enabled for the site.
 *
 * @param {array} alertData Alert data response from microservice.
 * @returns {array} Filtered alert data
 */
export function filterLiveAlertsBySupportedTypes(alertData) {
  if (window.weatherAlerts && window.weatherAlerts.alert_types) {
    const supportedTypes = window.weatherAlerts.alert_types.flatMap((type) => type.split(',').map((subType) => subType.trim())); // eslint-disable-line max-len
    return alertData.filter(
      (alert) => supportedTypes.includes(alert.weatherDetail.alertType),
    );
  }

  return alertData;
}
